import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { FixedCostCurrency } from "@/api/admin-api"

import { getLocalAiLeadGroupOverview } from "@/api/admin-api"
import { Building2 } from "@/components/icons"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuthGeneration } from "@/hooks/use-auth-generation"

import { DashboardSectionCard } from "./admin-dashboard-shared"
import { LocalAiLeadSteeringOverview } from "./local-ai-lead-steering-overview"

function money(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value)
  } catch {
    return `${value.toFixed(2)} ${currency}`
  }
}

export function LocalAiLeadGroupOverview({
  currency,
  canEditOverride = false,
}: {
  currency: FixedCostCurrency
  canEditOverride?: boolean
}) {
  const { t } = useTranslation()
  const authGeneration = useAuthGeneration()
  const query = useQuery({
    queryKey: ["admin", "local-ai-lead-group-overview", currency, authGeneration],
    queryFn: () => getLocalAiLeadGroupOverview(currency),
  })
  const groups = query.data?.groups ?? []
  const displayCurrency = query.data?.currency ?? currency
  const costMonth = query.data?.costMonth

  if (!query.isError && costMonth && query.data?.summary) {
    return (
      // Remount on a session change so the override draft cannot carry over.
      <LocalAiLeadSteeringOverview
        key={authGeneration}
        canEditOverride={canEditOverride}
        data={query.data}
      />
    )
  }

  return (
    <DashboardSectionCard
      icon={Building2}
      id="local-ai-lead-group-overview"
      description={t(
        costMonth
          ? "adminDashboard.localAiLeadSettings.groupOverviewAnnualDescription"
          : "adminDashboard.localAiLeadSettings.groupOverviewDescription",
        costMonth
          ? "Monthly cost and adoption for {{month}}. Shared costs include subscription, cloud infrastructure, and project work."
          : "One row per value of the grouping attribute, for the last 30 days. Shared cost is a monthly figure and is not changed by headcount.",
        { month: costMonth }
      )}
      title={t(
        "adminDashboard.localAiLeadSettings.groupOverviewTitle",
        "Cost and adoption per group"
      )}
    >
      {query.isLoading ? <Skeleton className="h-32 w-full" /> : null}
      {query.isError ? (
        <p className="text-destructive text-sm">
          {t(
            "adminDashboard.localAiLeadSettings.groupOverviewError",
            "Could not load the group overview."
          )}
        </p>
      ) : null}
      {!query.isLoading && groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.localAiLeadSettings.groupOverviewEmpty",
            "No usage in the last 30 days."
          )}
        </p>
      ) : null}
      {groups.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminDashboard.localAiLeadSettings.colGroup", "Group")}</TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colEmployees", "Employees")}
                </TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colUsers", "Users")}
                </TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colAdoption", "Adoption")}
                </TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colRequests", "Requests / user")}
                </TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colUsage", "Usage")}
                </TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colShared", "Shared")}
                </TableHead>
                {!costMonth ? (
                  <TableHead className="text-right">
                    {t("adminDashboard.localAiLeadSettings.colInfra", "Infra")}
                  </TableHead>
                ) : null}
                <TableHead className="text-right">
                  {t("adminDashboard.localAiLeadSettings.colTotal", "Total / month")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((row) => (
                <TableRow key={row.groupValue ?? "unassigned"}>
                  <TableCell>
                    {row.groupValue ??
                      t("adminDashboard.localAiLeadSettings.groupUnassigned", "No group")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.employees == null
                      ? t("adminDashboard.localAiLeadSettings.notReported", "Not reported")
                      : row.employees.toLocaleString("sv-SE")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.activeUsers}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.adoption == null ? "—" : `${row.adoption.toFixed(1)}%`}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.requestsPerUser == null ? "—" : row.requestsPerUser.toFixed(1)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(row.usageMonthly, displayCurrency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(row.sharedMonthly, displayCurrency)}
                  </TableCell>
                  {!costMonth ? (
                    <TableCell className="text-right tabular-nums">
                      {money(row.infraMonthly, displayCurrency)}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-right font-medium tabular-nums">
                    {money(row.totalMonthly, displayCurrency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </DashboardSectionCard>
  )
}
